import axios from 'axios';
import React, { ReactNode, createContext, useState, useContext } from 'react';
import { createAxiosInstance, publicAxios } from '../ApiClient';
import useActivityLogger from '../Hooks/useActivityLogger';
import Subscription from '../interfaces/Subscription';

import User from '../interfaces/User';

interface AuthContextInterface {
  token: string | null;
  setToken: React.Dispatch<React.SetStateAction<string | null>>;
  refreshToken: () => Promise<any>;
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  reloadUser: () => void;
  isSchoolUser: () => boolean;
  getSubscription: () => Subscription | null;
  reloadSubscription: () => Promise<void>;
  fetchSubscription: (user: User) => Promise<Subscription[]>;
  updateCredential: (user: User | null, token: string | null) => Promise<any>;
  hasPrivilege: (privilege: string) => boolean;
  signOut: () => Promise<void>;
}

interface Props {
  children?: ReactNode
}

const AuthContext = createContext<AuthContextInterface | null>(null);

const AuthProvider = ({ children }: Props) => {
  const [token, setToken] = useState<string | null>(null);

  const [user, setUser] = useState<User | null>(null);

  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);

  const postActivity = useActivityLogger();

  const refreshToken = async () => {
    try {
      const ret = await publicAxios.post('/api/users/refresh-token');
      console.log('new token', ret);
      await updateCredential(ret.data.user, ret.data.token);

      return ret.data.token;
    } catch (err) {
      await updateCredential(null, null);

      return null;
    }
  };

  const signOut = async () => {
    await axios.post('/api/users/signOut');
    postActivity('logout', {});
    updateCredential(null, null);
  }

  const reloadUser = async () => {
    if (user)
      await reloadUserBody(user);
  }

  const reloadUserBody = async (user: User) => {
    const newAxios = createAxiosInstance();
    newAxios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    const userDetail = await newAxios.get<User>(`/api/users/${user.id}`);
    if (userDetail)
      setUser(userDetail.data);

    console.log('reload user', userDetail.data);
    const ret = await newAxios.get<Subscription[]>('/api/subscriptions');

    const newSubscriptions: Subscription[] = ret.data;

    console.log('set subscriptions', newSubscriptions);
    setSubscriptions(newSubscriptions);
  }

  const updateCredential = async (user: User | null, token: string | null) => {
    setUser(user);
    setToken(token);
    setSubscriptions([]);

    if (user) {
      reloadUserBody(user);
    }
  }

  const reloadSubscription = async () => {
    if (!user) return;

    const ret = await fetchSubscription(user);
    setSubscriptions(ret);
  }

  const fetchSubscription = async (user: User) => {
    const ret = await axios.get<Subscription[]>('/api/subscriptions',
      { params: { user: user.id } });

    const subscriptions: Subscription[] = ret.data;

    return subscriptions;
  }

  const getSubscription = () => {
    if (!user) return null;

    if (subscriptions.length > 0) {
      return subscriptions[0];
    }

    return null;
  }

  const hasPrivilege = (privilege: string) => {
    if (!user) return false;

    const now = new Date();
    for (const subscription of subscriptions) {
      if (subscription.expiry && subscription.expiry < now) {
        continue;
      }

      if (subscription.role.privileges.includes(privilege)) {
        return true;
      }
    }

    return user?.role?.privileges?.includes(privilege) ?? false;
  }

  const isSchoolUser = () => {
    if (!user?.classes) return false;

    return user.classes.length > 0;
  }

  return (
    <AuthContext.Provider value={{
      token, setToken,
      user, setUser, reloadUser,
      isSchoolUser,
      getSubscription,
      reloadSubscription, fetchSubscription,
      updateCredential, hasPrivilege,
      refreshToken,
      signOut
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;

export const useAuthContext = () => {
  return useContext(AuthContext) as AuthContextInterface;
}
